import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  forwardRef,
  input,
} from '@angular/core';
import { FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { ShFormControl } from '../form-control/form-control.base';
import { primeSize } from '../models';

/**
 * Single-line text input. Read-only (itself or through its `sh-field`) it
 * shows the lock icon; `icon` puts a Font Awesome icon in front.
 *
 *   <sh-input [(value)]="name" />
 *   <sh-input type="email" icon="fa-solid fa-envelope" formControlName="email" />
 *
 * Attributes such as `required`, `name` and `e2e-id` go on the `sh-input`
 * element (forms validators see the host); `maxlength` and `autocomplete`
 * are inputs because they belong to the native element inside.
 */
@Component({
  selector: 'sh-input',
  changeDetection: ChangeDetectionStrategy.Default,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    InputTextModule,
    IconFieldModule,
    InputIconModule,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ShInputComponent),
      multi: true,
    },
  ],
  host: { class: 'sh-control', '[class.sh-invalid]': 'isInvalid()' },
  template: `
    <p-iconfield>
      <p-inputicon *ngIf="icon()" [styleClass]="icon()!"></p-inputicon>
      <input
        pInputText
        [type]="type()"
        [ngModel]="value()"
        (ngModelChange)="commit($event)"
        [ngModelOptions]="{ standalone: true }"
        [attr.id]="controlId()"
        [attr.placeholder]="placeholder() | translate"
        [attr.aria-label]="ariaLabel() | translate"
        [attr.maxlength]="maxlength()"
        [attr.autocomplete]="autocomplete()"
        [readOnly]="isReadonly()"
        [disabled]="isDisabled()"
        [pSize]="pSize()"
        [fluid]="true"
        (blur)="markTouched()"
      />
      <p-inputicon
        *ngIf="isReadonly()"
        styleClass="fa-solid fa-lock"
      ></p-inputicon>
    </p-iconfield>
  `,
})
export class ShInputComponent extends ShFormControl<string> {
  readonly type = input<'text' | 'email' | 'url'>('text');
  /** Font Awesome classes, shown in front of the text. */
  readonly icon = input<string>();
  readonly maxlength = input<number>();
  readonly autocomplete = input<string>();

  protected readonly pSize = computed(
    () => primeSize(this.size()) as 'small' | 'large'
  );
}
